import { DateTime } from "luxon";
import { classes } from "polytype";
import {
  AggregateMetaData,
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base";
import assertObjectFound from "../../base/assertObjectFound.js";
import { config } from "../../config/config.js";
import type { ContractListQuery } from "../../contract";
import { Contract } from "../../contract";
import { type DomFile, File, type FileAccessTokenProvider } from "../../file";
import type { InvoiceListQuery } from "../../invoice";
import { Invoice } from "../../invoice";
import type { ExtensionInstanceListQuery } from "../../marketplace";
import { ExtensionInstance } from "../../marketplace";
import type { OrderListQuery } from "../../order";
import { Order } from "../../order";
import { ProjectListQuery } from "../../project";
import type { AsyncResourceVariant } from "../../react";
import { provideReact } from "../../react";
import type { ServerListQuery } from "../../server";
import { Server } from "../../server";
import { ContractPartner } from "../ContractPartner";
import type {
  CustomerInviteCreateRequestData,
  CustomerInviteListQuery,
} from "../CustomerInvite";
import { CustomerInvite } from "../CustomerInvite";
import type {
  CustomerMembershipListQuery,
  CustomerRole,
} from "../CustomerMembership";
import { CustomerMembership } from "../CustomerMembership";
import { InvoiceSettings } from "../InvoiceSettings";
import type { CustomerPermission } from "../permissions.js";
import { customerPermissions } from "../permissions.js";
import { Wallet } from "../Wallet";
import { CustomerAvatarAccessTokenProvider } from "./CustomerAvatarAccessTokenProvider.js";
import type {
  ContractPartnerModelData,
  CustomerData,
  CustomerExecutingUserRoles,
  CustomerListItemData,
  CustomerListQueryData,
  CustomerVatIdValidationState,
} from "./types.js";

export class Customer extends ReferenceModel {
  public static aggregateMetaData = new AggregateMetaData(
    "customer",
    "customer",
  );
  public readonly fileAccessTokenProvider: FileAccessTokenProvider;
  public readonly servers: ServerListQuery;
  public readonly projects: ProjectListQuery;
  public readonly invites: CustomerInviteListQuery;
  public readonly memberships: CustomerMembershipListQuery;
  public readonly invoiceSettings: InvoiceSettings;
  public readonly contracts: ContractListQuery;
  public readonly invoices: InvoiceListQuery;
  public readonly orders: OrderListQuery;
  public readonly extensionInstances: ExtensionInstanceListQuery;

  public constructor(id: string) {
    super(id);
    this.fileAccessTokenProvider = new CustomerAvatarAccessTokenProvider(this);
    this.servers = Server.query({ customer: this });
    this.projects = new ProjectListQuery({
      customer: this,
    });
    this.invoiceSettings = InvoiceSettings.ofCustomerId(id);
    this.invites = CustomerInvite.query(this);
    this.memberships = CustomerMembership.query(this);
    this.contracts = Contract.query(this);
    this.invoices = Invoice.query(this);
    this.orders = Order.query({ customer: this });
    this.extensionInstances = ExtensionInstance.query({ customer: this });
  }

  public static findAggregate(customerId?: string) {
    return customerId
      ? { id: customerId, ...Customer.aggregateMetaData }
      : undefined;
  }

  public static ofId(id: string) {
    return new Customer(id);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.customer.find(id);
    if (data !== undefined) {
      return new CustomerDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const customer = await this.find(id);
    assertObjectFound(customer, "customer", id);
    return customer;
  });

  public findDetailed = provideReact(
    () => Customer.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<CustomerDetailed | undefined>>;

  public getDetailed = provideReact(
    () => Customer.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<CustomerDetailed>>;

  public static query(query: CustomerListQueryData = {}) {
    return new CustomerListQuery(query);
  }

  public static async create(data: {
    name: string;
    vatId?: string;
    owner?: ContractPartnerModelData;
  }) {
    const { name, vatId, owner } = data;

    const response = await config.behaviors.customer.create({
      vatId: vatId ?? undefined,
      owner: owner
        ? {
            ...owner,
            phoneNumbers: owner.phoneNumber ? [owner.phoneNumber] : undefined,
          }
        : undefined,
      name,
    });

    if ("id" in response) {
      return new Customer(response.id);
    }

    return response.map((e) => ({
      ...e,
      path:
        e.path === "owner.phoneNumbers"
          ? "owner.phoneNumber"
          : e.path === "address.zip"
            ? "owner.address.zip"
            : e.path,
    }));
  }

  public async update(data: {
    name: string;
    vatId?: string;
    owner?: ContractPartnerModelData;
  }) {
    const { name, vatId, owner } = data;

    const response = await config.behaviors.customer.update(this.id, {
      vatId,
      owner: owner
        ? {
            ...owner,
            phoneNumbers: owner.phoneNumber ? [owner.phoneNumber] : undefined,
          }
        : undefined,
      name,
    });

    if (!response) {
      return;
    }

    return response.map((e) => ({
      ...e,
      path:
        e.path === "owner.phoneNumbers"
          ? "owner.phoneNumber"
          : e.path === "address.zip"
            ? "owner.address.zip"
            : e.path,
    }));
  }

  public async delete() {
    await config.behaviors.customer.delete(this.id);
  }

  public async inviteMember(data: CustomerInviteCreateRequestData) {
    return CustomerInvite.create(this, data);
  }

  // todo: muss eigentlich auch für Rolle member erreichbar sein, um zb zu prüfen ob ein Projekt angelegt werden kann
  public isBankrupt = provideReact(async () => {
    const invoiceSettings = await InvoiceSettings.ofCustomerId(
      this.id,
    ).findDetailed();
    if (!invoiceSettings?.status) {
      return false;
    }

    return invoiceSettings.status.some((status) => status.type === "bankrupt");
  }, [this.id]);

  public getOwnMembership = provideReact(async () => {
    return await CustomerMembership.getOwn(this);
  }, [this.id]);

  public async uploadAvatar(file: DomFile) {
    await File.upload(file, this.fileAccessTokenProvider);
  }

  public getAvatarUploadRules = provideReact(async () => {
    return File.getUploadRules("avatar");
  });

  public async removeAvatar() {
    await config.behaviors.customer.removeAvatar(this.id);
  }

  public findWallet = provideReact(async () => {
    return await Wallet.find(this.id);
  }, [this.id]);

  public async createWallet() {
    await Wallet.create(this.id);
  }

  public async suggestReward(suggestion: string) {
    await config.behaviors.customer.createRecommendationSuggestion(
      this.id,
      suggestion,
    );
  }

  public findPaymentMethod = provideReact(async () => {
    return await config.behaviors.customer.findPaymentMethod(this.id);
  }, [this.id]);

  public async updatePaymentMethod(customReturnUrl: string) {
    return await config.behaviors.customer.updatePaymentMethod(
      this.id,
      customReturnUrl,
    );
  }

  public getBillingPortalLink = provideReact(async () => {
    return await config.behaviors.customer.getBillingPortalLink(this.id);
  }, [this.id]);
}

class CustomerCommon extends classes(
  DataModel<CustomerListItemData | CustomerData>,
  Customer,
) {
  public readonly name: string;
  public readonly contractPartner?: ContractPartner;
  public readonly avatar?: File;
  public readonly vatId?: string;
  public readonly isBanned?: boolean;
  public readonly suspendedSince?: DateTime;
  public readonly vatIdValidationState?: CustomerVatIdValidationState;
  public readonly projectCount?: number;
  public readonly memberCount?: number;
  public readonly creationDate?: string;
  public readonly executingUserRoles?: CustomerExecutingUserRoles[];
  public readonly avatarRefId?: string;
  public readonly customerNumber?: string;
  public readonly isInDefaultOfPayment?: boolean;
  public readonly ownRole: CustomerRole;
  public readonly isAllowedToPlaceOrders?: boolean;

  public constructor(data: CustomerListItemData | CustomerData) {
    super([data], [data.customerId]);
    this.name = data.name;
    if (data.owner) {
      this.contractPartner = new ContractPartner(data.owner);
    }
    this.avatar = data.avatarRefId ? File.ofId(data.avatarRefId) : undefined;
    this.vatId = data.vatId;
    this.isBanned = data.isBanned;
    if (data.activeSuspension) {
      this.suspendedSince = DateTime.fromISO(data.activeSuspension?.createdAt);
    }
    this.creationDate = data.creationDate;
    this.memberCount = data.memberCount;
    this.projectCount = data.projectCount;
    this.vatIdValidationState = data.vatIdValidationState;
    this.executingUserRoles = data.executingUserRoles;
    this.avatarRefId = data.avatarRefId;
    this.customerNumber = data.customerNumber;
    this.isInDefaultOfPayment = data.isInDefaultOfPayment;
    this.ownRole = data.executingUserRoles?.[0] ?? "notset";
    this.isAllowedToPlaceOrders = data.isAllowedToPlaceOrders;
  }

  public hasPermission(permission: CustomerPermission) {
    return customerPermissions[permission].includes(this.ownRole);
  }

  public async updateContractPartner(data: {
    owner: ContractPartnerModelData;
    vatId?: string;
  }) {
    const { owner, vatId } = data;

    return await this.update({ name: this.name, vatId, owner });
  }

  public async updateName(name: string) {
    return await this.update({
      name,
      vatId: this.vatId,
      owner: this.contractPartner
        ? {
            phoneNumber: this.contractPartner.phoneNumber,
            salutation: this.contractPartner.salutation ?? "other",
            address: { ...this.contractPartner.address },
            emailAddress: this.contractPartner.emailAddress,
            firstName: this.contractPartner.firstName,
            lastName: this.contractPartner.lastName,
            company: this.contractPartner.company,
          }
        : undefined,
    });
  }
}

export class CustomerDetailed extends classes(
  CustomerCommon,
  DataModel<CustomerData>,
) {
  public constructor(data: CustomerData) {
    super([data], [data]);
  }
}

export class CustomerListItem extends classes(
  CustomerCommon,
  DataModel<CustomerListItemData>,
) {
  public constructor(data: CustomerListItemData) {
    super([data], [data]);
  }
}

export class CustomerListQuery extends ListQueryModel<CustomerListQueryData> {
  public constructor(query: CustomerListQueryData = {}) {
    super(query);
  }

  public refine(query: CustomerListQueryData) {
    return new CustomerListQuery({
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items, totalCount } = await config.behaviors.customer.list({
      limit: config.defaultPaginationLimit,
      ...this.query,
    });

    return new CustomerList(
      this.query,
      items.map((d) => new CustomerListItem(d)),
      totalCount,
    );
  }, [this.queryId, "customer"]);

  public getTotalCount = provideReact(async () => {
    const { totalCount } = await this.refine({ limit: 1 }).execute();
    return totalCount;
  }, [this.queryId, "customer"]);
}

export class CustomerList extends classes(
  CustomerListQuery,
  ListDataModel<CustomerListItem>,
) {
  public constructor(
    query: CustomerListQueryData,
    customers: CustomerListItem[],
    totalCount: number,
  ) {
    super([query], [customers, totalCount]);
  }
}
